import { Button, Col, Descriptions, Divider, Pagination, Result, Row, Tag, Typography } from 'antd';
import ContainerLookupDetailsDto from '../../models/ContainerLookupDetailsDto';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { ExclamationCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { FormContainer, FormFooterContainer, PrimaryButton, StyledIcon } from '../Styled/GlobalStyles';
import { DataRow } from './components/DataRow';
import { LabelCol } from './components/LabelCol';
import { ValueCol } from './components/ValueCol';
import { CheckMarkCol } from './components/CheckMarkCol';
import { ErrorResponseDto } from '../../models/ErrorResponseDto';
import { NotesTextArea, StyledHeaderRowCol } from './ContainerLookupStyles';
import { useEffect, useState } from 'react';
import { logEvent } from '../../util/Analytics';
import FloatingActionButton from './components/FloatingActionButton';
import styled from 'styled-components';

const StyledFloatingActionButtonWrapper = styled.div`
  display: flex;
  height: 10px;
`;

const ContainerLookupDetails = ({
  containerDetailsList,
  containerDetailsError,
  resetContainerDetailsForm,
}: {
  containerDetailsList: ContainerLookupDetailsDto[];
  containerDetailsError: ErrorResponseDto | null;
  resetContainerDetailsForm: () => void;
}) => {
  const [containerDetails, setContainerDetails] = useState<ContainerLookupDetailsDto | null>(null);

  // Set Initial Container Details
  useEffect(() => {
    if (containerDetailsList.length > 0) {
      setContainerDetails(containerDetailsList[0]);
    }
  }, [containerDetailsList]);

  const onPaginationChange = (page: number, pageSize?: number | undefined) => {
    setContainerDetails(containerDetailsList[page - 1]);
  };

  const userClickedNewSearch = () => {
    logEvent('container_lookup_new_search');
    resetContainerDetailsForm();
  };

  const userClickedBackButton = () => {
    logEvent('container_lookup_back_button');
    resetContainerDetailsForm();
  };

  return (
    <>
      {containerDetailsError && (
        <FormContainer>
          <Result
            status='error'
            title='Container Not Found'
            subTitle={containerDetailsError.message}
            icon={<QuestionCircleOutlined />}
            extra={[
              <PrimaryButton type='primary' key='console' onClick={resetContainerDetailsForm}>
                Search Again
              </PrimaryButton>,
            ]}
          ></Result>
        </FormContainer>
      )}
      {containerDetailsList.length != 0 && containerDetails && (
        <>
          <FormContainer>
            {containerDetailsList.length > 1 && (
              <Row>
                <Col flex='auto'>
                  <ExclamationCircleOutlined style={{ fontSize: 20 }} />
                  <Typography.Text style={{ fontWeight: 700, marginLeft: 20 }}>
                    Multiple Containers Found.
                  </Typography.Text>
                </Col>
                <Col flex={'200px'}>
                  <Pagination
                    defaultCurrent={1}
                    total={containerDetailsList.length}
                    simple
                    pageSize={1}
                    onChange={onPaginationChange}
                  />
                </Col>
              </Row>
            )}

            <div style={{ padding: 20, paddingBottom: 0 }}>
              <DataRow justify='space-between'>
                <LabelCol span={6} label='Container Number' />
                <ValueCol span={8}>
                  <StyledIcon icon={solid('hashtag')} color={'#8b97a8'} />
                  {containerDetails.containerNumber}
                </ValueCol>

                <LabelCol span={4}>
                  <StyledIcon icon={solid('location-dot')} />
                  Location
                </LabelCol>
                <ValueCol span={4} value={containerDetails.siteName} />
              </DataRow>
              <Divider style={{ borderBlockStart: '2px solid rgba(5,5,5, 0.16)' }} />
            </div>
            <div style={{ padding: 20, paddingTop: 0 }}>
              <DataRow>
                <LabelCol label='Container Number' />
                <ValueCol value={containerDetails.containerNumber} />
              </DataRow>
              <DataRow>
                <LabelCol label='Bill Of Lading' />
                <ValueCol value={containerDetails.billOfLading} span={10} />
                <StyledFloatingActionButtonWrapper>
                  <FloatingActionButton containerDetails={containerDetails} />
                </StyledFloatingActionButtonWrapper>
              </DataRow>
              <DataRow>
                <LabelCol label='CBP Exam(s)' />
                <ValueCol value={containerDetails.cbpExams} />
              </DataRow>
              <DataRow>
                <LabelCol label='Container Type' />
                <ValueCol>
                  <span>{containerDetails.containerType}</span>
                  {containerDetails.isHazardous && (
                    <Tag color='error' style={{ marginLeft: 15 }} icon={<ExclamationCircleOutlined />}>
                      Hazardous
                    </Tag>
                  )}
                </ValueCol>
              </DataRow>
              <DataRow>
                <LabelCol label='Chassis #' />
                <ValueCol value={containerDetails.chassisNumber} span={4} />
                {['002', '006', '009', '011'].includes(containerDetails.siteCode) && (
                  <>
                    {containerDetails.chassisNumber.toUpperCase().startsWith('SFWY') ? (
                      <Tag color='volcano' style={{ margin: 0 }}>
                        Own chassis required
                      </Tag>
                    ) : (
                      <Tag color='green' style={{ margin: 0 }}>
                        No chassis required
                      </Tag>
                    )}
                  </>
                )}
              </DataRow>
              <DataRow>
                <LabelCol label='Steamship Line' />
                <ValueCol value={containerDetails.steamshipLine} />
              </DataRow>
              <DataRow>
                <LabelCol label='Vessel' />
                <ValueCol value={containerDetails.vesselCode} />
              </DataRow>
              <DataRow>
                <LabelCol label='Terminal' />
                <ValueCol value={containerDetails.terminal} />
              </DataRow>
            </div>
            <div style={{ height: '32px', backgroundColor: '#918faa' }}>
              <StyledHeaderRowCol paddingleft={30}>Container Status</StyledHeaderRowCol>
            </div>

            <div style={{ padding: 20, paddingTop: 0 }}>
              <DataRow>
                <LabelCol label='Available at Terminal' />
                <ValueCol
                  isDate={true}
                  value={`${containerDetails.availableAtTerminalDate} ${containerDetails.availableAtTerminalTime}`}
                />
              </DataRow>
              <DataRow>
                <LabelCol label='CES Gate-In' />
                <ValueCol isDate={true} value={`${containerDetails.cesGateInDate} ${containerDetails.cesGateInTime}`} />
              </DataRow>
              <DataRow>
                <LabelCol label='CES In Door' />
                <ValueCol isDate={true} value={`${containerDetails.cesInDoorDate} ${containerDetails.cesInDoorTime}`} />
              </DataRow>
              <DataRow>
                <LabelCol label='CBP Exam Completed' />
                <ValueCol isDate={true} value={containerDetails.cbpExamCompleteDate} />
              </DataRow>
              <DataRow>
                <LabelCol label='Last Free Day @ CES' />
                <ValueCol isDate={true} value={containerDetails.lastFreeDayAtCes} />
              </DataRow>
              {containerDetails.railLocationDeliveryLocation && (
                <DataRow>
                  <LabelCol label='Rail Move Location' />
                  <ValueCol value={containerDetails.railLocationDeliveryLocation} span={5} />
                  <CheckMarkCol isChecked={!!containerDetails.railLocationDeliveryLocation} />
                </DataRow>
              )}
            </div>
            <div style={{ height: '32px', backgroundColor: '#918faa' }}>
              <Row>
                <StyledHeaderRowCol span={12} paddingleft={30}>
                  Release Details
                </StyledHeaderRowCol>
                <StyledHeaderRowCol span={6}>Available for Pickup?</StyledHeaderRowCol>
              </Row>
            </div>
            <div style={{ padding: 20, paddingTop: 0 }}>
              <DataRow>
                <LabelCol label='Loaded' />
                <ValueCol isDate={true} value={containerDetails.loadedDate} span={5} />
                <CheckMarkCol isChecked={!!containerDetails.loadedDate} />
              </DataRow>
              <DataRow>
                <LabelCol label='Customs Release' />
                <ValueCol isDate={true} value={containerDetails.customsReleaseDate} span={5} />
                <CheckMarkCol isChecked={!!containerDetails.customsReleaseDate} />
              </DataRow>
              <DataRow>
                <LabelCol label='Freight Release' />
                <ValueCol isDate={true} value={containerDetails.freightReleaseDate} span={5} />
                <CheckMarkCol isChecked={!!containerDetails.freightReleaseDate} />
              </DataRow>
              <DataRow>
                <LabelCol label='Entry Release' />
                <ValueCol isDate={true} value={containerDetails.entryReleaseDate} span={5} />
                <CheckMarkCol isChecked={!!containerDetails.entryReleaseDate} />
              </DataRow>
              <DataRow>
                <LabelCol label='Payment Status' />
                <ValueCol value={''} span={5} />
                <CheckMarkCol isChecked={!!containerDetails.paymentStatus} />
              </DataRow>
              <DataRow>
                <LabelCol label='Outbound Seal' />
                <ValueCol value={containerDetails.outboundSealNumber} />
              </DataRow>
              <DataRow>
                <LabelCol label='Gate Out' />
                <ValueCol isDate={true} value={`${containerDetails.gateOutDate} ${containerDetails.gateOutTime}`} />
              </DataRow>
              <DataRow>
                <LabelCol label='Carrier' />
                <ValueCol value={containerDetails.carrier} />
              </DataRow>
              <DataRow>
                <LabelCol label='Notes' />
                <ValueCol>
                  <NotesTextArea placeholder='' disabled value={containerDetails.notes} />
                </ValueCol>
              </DataRow>
            </div>
            <div style={{ padding: 20, paddingBottom: 0, paddingTop: 0 }}>
              <Divider style={{ borderBlockStart: '2px solid rgba(5,5,5, 0.16)' }} />
              <p style={{ color: '#8b97a8', fontSize: '12px', width: '80%' }}>
                <span style={{ fontWeight: 800 }}>Demurrage costs</span> are applicable if a container is picked up
                after the <span style={{ fontWeight: 800 }}>Last Free Day</span>. Please refer to our tariff for
                demurrage charges or contact our customer service. Upon completion of the container examination by CBP,
                CES will provide "Free Days" as defined below:
              </p>
              <Descriptions layout='vertical' bordered>
                <Descriptions.Item label='' style={{ color: '#8b97a8' }}>
                  All Exam Types
                </Descriptions.Item>
                <Descriptions.Item label='Dry' style={{ color: '#231f55' }}>
                  <span style={{ fontWeight: 700 }}>
                    {['ELIZABETH', 'BALTIMORE'].includes(containerDetails.siteName) ? '3' : '2'} Days
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label='Refrigerated' style={{ color: '#231f55' }}>
                  <span style={{ fontWeight: 700 }}>
                    {['BALTIMORE'].includes(containerDetails.siteName) ? '3' : '2'} Days
                  </span>
                </Descriptions.Item>
              </Descriptions>
            </div>
          </FormContainer>
          <FormFooterContainer>
            <Button type='text' onClick={userClickedBackButton}>
              Back
            </Button>

            <div style={{ float: 'right', width: '200px' }}>
              <Button
                type='primary'
                htmlType='submit'
                style={{ backgroundColor: '#4f45c7', height: '40px' }}
                block
                onClick={userClickedNewSearch}
              >
                Run A New Search
              </Button>
            </div>
          </FormFooterContainer>
        </>
      )}
    </>
  );
};

export default ContainerLookupDetails;
